import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card, CardContent, Chip, Stack, Typography, Alert, Skeleton, Box,
  Avatar, Table, TableRow, TableCell, TableBody, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, useTheme, useMediaQuery,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";

interface IntegrationItem {
  integration: string;
  resource: string;
  status: "ok" | "warn" | "error" | "unknown";
  status_label: string;
  last_success_at?: string;
  last_run_at?: string;
  age_minutes?: number | null;
  rows?: number | null;
  error?: string | null;
  batch_id?: string;
  details?: string;
}

interface Response {
  tenant: string;
  integration_source?: string;
  integrations: IntegrationItem[];
  stale: boolean;
  error?: string;
}

const META: Record<IntegrationItem["status"], { color: "success" | "warning" | "error" | "default"; Icon: any; label: string }> = {
  ok:      { color: "success", Icon: CheckCircleRoundedIcon,  label: "Ativa" },
  warn:    { color: "warning", Icon: WarningAmberRoundedIcon, label: "Atrasada" },
  error:   { color: "error",   Icon: ErrorRoundedIcon,        label: "Erro" },
  unknown: { color: "default", Icon: HelpOutlineRoundedIcon,  label: "?" },
};

function friendlyAgo(mins: number | null | undefined): string {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min atrás`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  return `${d}d atrás`;
}

function friendlyTs(v?: string): string {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

export function Integrations() {
  const { tenant } = useTenant();
  const api = useApi();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [filter, setFilter] = useState<"todos" | IntegrationItem["status"]>("todos");

  const q = useQuery({
    queryKey: ["integrations", tenant],
    queryFn: () => api.call<Response>("/api/integrations"),
    enabled: !!tenant, staleTime: 30_000, retry: false,
  });

  const all = q.data?.integrations ?? [];
  const items = all.filter((i) => filter === "todos" || i.status === filter);
  const counts = {
    ok:    all.filter((i) => i.status === "ok").length,
    warn:  all.filter((i) => i.status === "warn").length,
    error: all.filter((i) => i.status === "error").length,
  };

  return (
    <>
      <PageHeader
        eyebrow="Observabilidade"
        title="Integrações"
        subtitle={<>Origem do dado: <strong>{q.data?.integration_source || tenant || "?"}</strong> · quando a base foi publicada e se a conferência passou</>}
      />

      {q.data?.stale && (
        <Alert severity={q.data.error === "no_dataset" ? "info" : "warning"} sx={{ borderRadius: 2 }}>
          {q.data.error === "no_dataset" ? "Este tenant ainda não tem integrações configuradas." :
           `Status em cache ou fora do ar (${q.data.error ?? "unknown"}).`}
        </Alert>
      )}

      {/* Summary chips */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1.5}>
        <StatusSummary color="success" Icon={CheckCircleRoundedIcon} count={counts.ok}    label="Ativas"   onClick={() => setFilter("ok")}    active={filter === "ok"} />
        <StatusSummary color="warning" Icon={WarningAmberRoundedIcon} count={counts.warn} label="Atrasadas" onClick={() => setFilter("warn")}  active={filter === "warn"} />
        <StatusSummary color="error"   Icon={ErrorRoundedIcon}        count={counts.error} label="Erro"     onClick={() => setFilter("error")} active={filter === "error"} />
        <Chip
          icon={<CloudSyncRoundedIcon />}
          label={`Todos (${all.length})`}
          onClick={() => setFilter("todos")}
          color={filter === "todos" ? "primary" : "default"}
          variant={filter === "todos" ? "filled" : "outlined"}
        />
      </Stack>

      {/* List */}
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {q.isLoading && Array.from({ length: 4 }).map((_, i) => (
            <Box key={i} sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Skeleton width="50%" height={20} />
              <Skeleton width="30%" height={14} />
            </Box>
          ))}
          {!q.isLoading && items.length === 0 && (
            <Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body2">Nenhuma integração no filtro atual.</Typography>
            </Box>
          )}
          {items.map((i, idx) => {
            const M = META[i.status];
            const showError = i.status === "error" && i.error;
            return (
              <Accordion
                key={idx}
                disableGutters
                elevation={0}
                square
                sx={{
                  borderTop: idx === 0 ? "none" : "1px solid",
                  borderColor: "divider",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{ px: 2, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 2 } }}
                >
                  <Avatar sx={{ bgcolor: `${M.color}.light`, color: `${M.color}.main`, width: 34, height: 34 }}>
                    <M.Icon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.resource}</Typography>
                      <Chip label={i.integration} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {friendlyAgo(i.age_minutes)} · última sync {friendlyTs(i.last_success_at)}
                      {i.rows != null && ` · ${i.rows.toLocaleString("pt-BR")} rows`}
                    </Typography>
                  </Box>
                  <Chip
                    label={i.status_label || M.label}
                    size="small"
                    color={M.color as any}
                    variant={i.status === "ok" ? "outlined" : "filled"}
                    sx={{ flexShrink: 0 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: "background.default", pt: 0 }}>
                  {showError && (
                    <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
                      <strong>Erro:</strong> {i.error}
                    </Alert>
                  )}
                  <Table size="small">
                    <TableBody>
                      <RowKV label="Última sync com sucesso" value={friendlyTs(i.last_success_at)} />
                      <RowKV label="Última execução" value={friendlyTs(i.last_run_at)} />
                      <RowKV label="Linhas processadas" value={i.rows != null ? i.rows.toLocaleString("pt-BR") : "—"} />
                      {!isSm && (
                        <>
                          <RowKV label="Batch ID" value={<code style={{ fontSize: 11 }}>{i.batch_id || "—"}</code>} />
                          {i.details && (
                            <RowKV
                              label="Detalhes"
                              value={<Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{i.details}</Typography>}
                            />
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {/* Este texto listava `ops_sync_state`, `gcs_ingestion_state`,
            `oracle_ops_sync_runs` e `varejofacil_sync_state` — tabelas do
            BigQuery, que nao existe mais. A base vive no S3 e e lida com
            DuckDB; o que esta tela mostra hoje e a publicacao dessa base. */}
        <strong>De onde vem:</strong> o manifesto da base canônica no lago — origem, quando foi publicada, quantas tabelas entraram e o resultado da conferência. A publicação é diária: <strong>Atualizada</strong> até 24h; <strong>Publicação atrasada</strong> entre 24h e 72h; <strong>Publicação parada</strong> depois disso, ou <strong>Conferência com divergência</strong> se a carga fechou com problema. É a mesma base que a Sophia lê no WhatsApp — se ela está velha aqui, a resposta de lá também está.
      </Alert>
    </>
  );
}

function StatusSummary({
  color, Icon, count, label, onClick, active,
}: { color: "success" | "warning" | "error"; Icon: any; count: number; label: string; onClick: () => void; active: boolean }) {
  return (
    <Tooltip title={`Filtrar por ${label}`}>
      <Chip
        icon={<Icon sx={{ fontSize: "16px !important" }} />}
        label={`${label}: ${count}`}
        onClick={onClick}
        color={active ? color : "default"}
        variant={active ? "filled" : "outlined"}
        sx={{ fontWeight: 600 }}
      />
    </Tooltip>
  );
}

function RowKV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell sx={{ color: "text.secondary", width: 200, fontSize: 12, borderBottom: "1px solid", borderColor: "divider" }}>{label}</TableCell>
      <TableCell sx={{ fontSize: 12 }}>{value}</TableCell>
    </TableRow>
  );
}
